/**
 * Toast utility that uses parent app's toast when available, 
 * otherwise falls back to local toast for standalone mode.
 * 
 * This ensures consistent toast styling and behavior when the stock app
 * is embedded in a parent app, while still working in standalone mode.
 * 
 * Usage:
 *   import { getToast } from './utils/toast';
 *   const toast = getToast();
 *   toast.success("Operation completed!");
 */

// Track if we've already logged to avoid spam
let hasLoggedToastSource = false;
let toastSourceLogTimeout = null;

export const getToast = () => {
  // Try to use parent app's toast first (when embedded)
  if (window.__PARENT_TOAST__) {
    // Log only once with a small delay to avoid spam
    if (!hasLoggedToastSource) {
      hasLoggedToastSource = true;
      if (toastSourceLogTimeout) clearTimeout(toastSourceLogTimeout);
      toastSourceLogTimeout = setTimeout(() => {
        console.log('[Toast] Using parent app toast');
      }, 500);
    }
    return window.__PARENT_TOAST__;
  }
  
  // Fallback to local toast if parent is not available (standalone mode)
  try {
    const { toast } = require('react-toastify');
    // Log only once with a small delay to avoid spam
    if (!hasLoggedToastSource) {
      hasLoggedToastSource = true;
      if (toastSourceLogTimeout) clearTimeout(toastSourceLogTimeout);
      toastSourceLogTimeout = setTimeout(() => {
        console.log('[Toast] Using stock app toast (standalone mode)');
      }, 500);
    }
    return toast;
  } catch (e) {
    console.warn('[Toast] Toast not available - react-toastify not found');
    // Return a no-op function to prevent errors
    return () => {};
  }
};

/**
 * Wait for parent toast to be available (useful for async loading scenarios)
 * @param {number} timeout - Maximum time to wait in milliseconds (default: 2000)
 * @returns {Promise} Promise that resolves with the toast function
 */
let hasLoggedWaitStatus = false;
let waitLogTimeout = null;

export const waitForParentToast = (timeout = 2000) => {
  return new Promise((resolve) => {
    if (window.__PARENT_TOAST__) {
      if (!hasLoggedWaitStatus) {
        hasLoggedWaitStatus = true;
        if (waitLogTimeout) clearTimeout(waitLogTimeout);
        waitLogTimeout = setTimeout(() => {
          console.log('[Toast] Parent toast available immediately');
        }, 500);
      }
      resolve(window.__PARENT_TOAST__);
      return;
    }
    
    if (!hasLoggedWaitStatus) {
      hasLoggedWaitStatus = true;
      if (waitLogTimeout) clearTimeout(waitLogTimeout);
      waitLogTimeout = setTimeout(() => {
        console.log('[Toast] Waiting for parent toast...');
      }, 500);
    }
    
    const checkInterval = setInterval(() => {
      if (window.__PARENT_TOAST__) {
        clearInterval(checkInterval);
        setTimeout(() => {
          console.log('[Toast] Parent toast found after waiting');
        }, 500);
        resolve(window.__PARENT_TOAST__);
      }
    }, 50);
    
    // Timeout after specified time
    setTimeout(() => {
      clearInterval(checkInterval);
      // Resolve with local toast if parent never becomes available
      try {
        const { toast } = require('react-toastify');
        setTimeout(() => {
          console.log('[Toast] Timeout reached, using stock app toast');
        }, 500);
        resolve(toast);
      } catch (e) {
        console.warn('[Toast] Failed to load toast, using no-op');
        resolve(() => {});
      }
    }, timeout);
  });
};

/**
 * Get debug information about the current toast setup
 * @returns {Object} Debug information object
 */
export const getToastDebugInfo = () => {
  const info = {
    hasParent: !!window.__PARENT_TOAST__,
    isEmbedded: !!window.__PARENT_TOAST__,
    toastSource: window.__PARENT_TOAST__ ? 'parent' : 'local'
  };
  
  console.log('[Toast Debug]', info);
  return info;
};
